import { useState } from 'react';
import { Mission, MissionStop } from '../data/missions';
import { STATIONS } from '../data/stations';
import { parseMissionsFromCSV, parseMissionsFromPDF } from '../utils/csvParser';

interface MissionManagerProps {
    missions: Mission[];
    onAdd: (mission: Mission) => void;
    onAddMultiple?: (missions: Mission[]) => void;
    onUpdate?: (mission: Mission) => void;
    onDelete: (id: string) => void;
}

export default function MissionManager({ missions, onAdd, onAddMultiple, onUpdate, onDelete }: MissionManagerProps) {
    const [trainNumber, setTrainNumber] = useState('');
    const [lineName, setLineName] = useState('Calvi - Ile-Rousse');
    const [stops, setStops] = useState<MissionStop[]>([]);

    // État temporaire pour l'ajout d'arrêt
    const [currentStationName, setCurrentStationName] = useState(STATIONS[0].name);
    const [arrivalTime, setArrivalTime] = useState('08:00');

    // Import CSV State
    const [isUploading, setIsUploading] = useState(false);
    const [importedMissions, setImportedMissions] = useState<Mission[]>([]);
    const [conflictingMissions, setConflictingMissions] = useState<{ newMission: Mission, existingId: string }[]>([]);

    // Édition Inline State
    const [editingMissionId, setEditingMissionId] = useState<string | null>(null);
    const [editTrainNumber, setEditTrainNumber] = useState('');
    const [editLineName, setEditLineName] = useState('');
    const [editStops, setEditStops] = useState<MissionStop[]>([]);

    // Pour ajouter un arrêt pendant l'édition
    const [editCurrentStation, setEditCurrentStation] = useState(STATIONS[0].name);
    const [editArrivalTime, setEditArrivalTime] = useState('08:00');

    const handleStartEdit = (mission: Mission) => {
        setEditingMissionId(mission.id);
        setEditTrainNumber(mission.trainNumber);
        setEditLineName(mission.lineName);
        setEditStops([...mission.stops]);
    };

    const handleSaveEdit = () => {
        if (!editingMissionId || !onUpdate) return;

        onUpdate({
            id: editingMissionId,
            trainNumber: editTrainNumber,
            lineName: editLineName,
            stops: editStops
        });

        setEditingMissionId(null);
    };

    const handleCancelEdit = () => {
        setEditingMissionId(null);
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploading(true);

        try {
            const isPdf = file.name.toLowerCase().endsWith('.pdf');
            const parsedMissions = isPdf 
                ? await parseMissionsFromPDF(file)
                : await parseMissionsFromCSV(file);

            if (parsedMissions.length === 0) {
                alert(`Aucune mission valide n'a été détectée dans ce ${isPdf ? 'PDF' : 'CSV'}. Assurez-vous d'avoir les numéros de trains en colonnes et les gares en lignes.`);
            } else {
                const existingMissionsMap = new Map(missions.map(m => [m.trainNumber.toLowerCase().trim(), m.id]));
                const pendingTrainNumbers = new Set(importedMissions.map(m => m.trainNumber.toLowerCase().trim()));

                const newMissions: Mission[] = [];
                const newConflicts: { newMission: Mission, existingId: string }[] = [];

                parsedMissions.forEach(m => {
                    const normNum = m.trainNumber.toLowerCase().trim();
                    if (pendingTrainNumbers.has(normNum)) {
                        // Deja dans la file d'attente d'importation, on ignore
                        return;
                    }

                    // On l'ajoute immédiatement pour éviter d'importer le même train 2 fois
                    // si le fichier CSV contient lui-même des doublons (ex: 2 colonnes avec le numéro 308)
                    pendingTrainNumbers.add(normNum);

                    if (existingMissionsMap.has(normNum)) {
                        newConflicts.push({ newMission: m, existingId: existingMissionsMap.get(normNum)! });
                    } else {
                        newMissions.push(m);
                    }
                });

                if (newConflicts.length > 0) {
                    setConflictingMissions(prev => [...prev, ...newConflicts]);
                }

                if (newMissions.length > 0) {
                    setImportedMissions(prev => [...prev, ...newMissions]);
                }
            }
        } catch (err) {
            console.error("Erreur lors de l'analyse du document:", err);
            alert("Erreur lors de la lecture du fichier. Assurez-vous d'importer un CSV ou un PDF valide.");
        } finally {
            setIsUploading(false);
            e.target.value = ''; // Reset input
        }
    };

    const handleUpdateImportedMissionNumber = (index: number, newNumber: string) => {
        const updated = [...importedMissions];
        updated[index] = { ...updated[index], trainNumber: newNumber };
        setImportedMissions(updated);
    };

    const handleReplaceConflict = (conflictIndex: number) => {
        const conflict = conflictingMissions[conflictIndex];
        // On transfère la mission conflictuelle dans la file d'attente normale (en attente de sauvegarde)
        // en lui donnant l'ID de l'ancienne mission pour qu'elle soit "écrasée" lors de l'upsert
        setImportedMissions(prev => [...prev, { ...conflict.newMission, id: conflict.existingId }]);
        setConflictingMissions(prev => prev.filter((_, i) => i !== conflictIndex));
    };

    const handleIgnoreConflict = (conflictIndex: number) => {
        setConflictingMissions(prev => prev.filter((_, i) => i !== conflictIndex));
    };

    const handleConfirmImport = async () => {
        if (onAddMultiple) {
            await onAddMultiple(importedMissions);
        } else {
            importedMissions.forEach(m => onAdd(m));
            alert(`${importedMissions.length} mission(s) importée(s) avec succès !`);
        }
        setImportedMissions([]);
    };

    const handleCancelImport = () => {
        setImportedMissions([]);
        setConflictingMissions([]);
    };

    const handleAddStop = () => {
        const stationData = STATIONS.find(s => s.name === currentStationName);
        if (!stationData) return;

        setStops([...stops, {
            stationName: currentStationName,
            arrivalTime: arrivalTime,
            pk: stationData.pk
        }]);
    };

    const handleRemoveStop = (index: number) => {
        setStops(stops.filter((_, i) => i !== index));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Vérification des doublons manuelle
        if (missions.some(m => m.trainNumber.toLowerCase().trim() === trainNumber.toLowerCase().trim())) {
            alert(`Attention : la mission n°${trainNumber} existe déjà. Impossible de créer un doublon.`);
            return;
        }

        onAdd({
            id: Date.now().toString(),
            trainNumber,
            lineName,
            stops
        });
        setTrainNumber('');
        setStops([]);
    };

    return (
        <div className="h-full flex flex-col p-4 space-y-4">
            <h2 className="text-white font-black italic text-xs tracking-tighter uppercase mb-2">
                Programmation des Missions
            </h2>

            {/* Zone Import CSV/PDF - Toujours affichée en haut pour permettre l'accumulation */}
            <div className="bg-cyan-900/20 border border-cyan-500/30 p-4 rounded-2xl border-dashed relative group mb-4 shrink-0 transition-colors hover:bg-cyan-900/40">
                <input
                    type="file"
                    accept=".csv,.pdf"
                    onChange={handleFileUpload}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    disabled={isUploading}
                    title="Ajouter un autre fichier CSV ou PDF"
                />
                <div className="flex flex-col items-center justify-center text-center space-y-2">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${isUploading ? 'bg-cyan-500/20 animate-pulse' : 'bg-cyan-500/10 group-hover:bg-cyan-500/20'}`}>
                        {isUploading ? (
                            <svg className="w-5 h-5 text-cyan-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : (
                            <span className="text-cyan-400 font-bold text-lg">📊</span>
                        )}
                    </div>
                    <div>
                        <p className="text-xs font-bold text-cyan-400 uppercase tracking-wider">
                            {isUploading ? 'Analyse du document...' : 'Importer Fiche Horaires (CSV ou PDF)'}
                        </p>
                        <p className="text-[9px] text-slate-500">Déposez un ou plusieurs fichiers Excel/CSV/PDF ici pour cumuler les missions</p>
                    </div>
                </div>
            </div>

            {/* Interface de Résolution de Conflits */}
            {conflictingMissions.length > 0 && (
                <div className="bg-orange-900/20 border border-orange-500 p-4 rounded-2xl space-y-4 animate-in fade-in slide-in-from-top-4 duration-500 mb-4">
                    <div className="flex justify-between items-center">
                        <div>
                            <h3 className="text-orange-400 font-bold text-sm uppercase">Conflits Détectés ({conflictingMissions.length})</h3>
                            <p className="text-[9px] text-slate-400">Ces numéros de train existent déjà dans le système.</p>
                        </div>
                    </div>

                    <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                        {conflictingMissions.map((conflict, idx) => (
                            <div key={idx} className="bg-black/40 border border-orange-500/30 p-3 rounded-xl flex items-center justify-between">
                                <div className="flex flex-col">
                                    <span className="text-white font-bold text-xs">Train N°{conflict.newMission.trainNumber}</span>
                                    <span className="text-[10px] text-slate-500 italic truncate">{conflict.newMission.lineName}</span>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => handleIgnoreConflict(idx)} className="text-[10px] text-slate-400 hover:text-white uppercase font-bold px-3 py-2 transition-colors">Ignorer</button>
                                    <button onClick={() => handleReplaceConflict(idx)} className="bg-orange-600 hover:bg-orange-500 text-white text-[10px] font-bold uppercase px-4 py-2 rounded-lg shadow-lg shadow-orange-500/20 transition-all active:scale-95">Remplacer</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Interface de Révision (Affichée seulement s'il y a des missions à réviser) */}
            {importedMissions.length > 0 && (
                <div className="bg-cyan-900/10 border border-cyan-500 p-4 rounded-2xl space-y-4 animate-in fade-in slide-in-from-top-4 duration-500 mb-4">
                    <div className="flex justify-between items-center">
                        <div>
                            <h3 className="text-cyan-400 font-bold text-sm uppercase">Révision de l'Importation</h3>
                            <p className="text-[9px] text-slate-400">{importedMissions.length} mission(s) en attente. Vous pouvez encore ajouter d'autres fichiers.</p>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={handleCancelImport} className="text-[10px] text-slate-500 hover:text-white uppercase font-bold px-3 py-2 transition-colors">Vider la liste</button>
                            <button onClick={handleConfirmImport} className="bg-cyan-500 hover:bg-cyan-400 text-white text-[10px] font-bold uppercase px-4 py-2 rounded-lg shadow-lg shadow-cyan-500/20 transition-all active:scale-95">Valider Tout</button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                        {importedMissions.map((m, idx) => (
                            <div key={idx} className="bg-black/40 border border-white/5 p-3 rounded-xl flex flex-col space-y-2">
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-slate-500 font-bold uppercase">Train N°</span>
                                    <input
                                        type="text"
                                        value={m.trainNumber}
                                        onChange={(e) => handleUpdateImportedMissionNumber(idx, e.target.value)}
                                        className="bg-slate-900 border border-cyan-500/30 rounded px-2 py-1 text-xs text-cyan-400 font-mono w-20 focus:border-cyan-500 outline-none transition-colors"
                                    />
                                    <span className="text-[10px] text-slate-600 italic flex-1 truncate">{m.lineName}</span>
                                </div>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 py-1 border-t border-white/5 mt-1">
                                    {m.stops.map((s, sIdx) => (
                                        <div key={sIdx} className="flex justify-between text-[9px]">
                                            <span className="text-slate-500 truncate mr-1">{s.stationName}</span>
                                            <span className="text-slate-300 font-mono">{s.arrivalTime}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}


            <div className="flex items-center gap-4 py-2">
                <div className="h-[1px] flex-1 bg-white/10"></div>
                <span className="text-[9px] text-slate-600 font-bold uppercase tracking-widest">OU MANUELLEMENT</span>
                <div className="h-[1px] flex-1 bg-white/10"></div>
            </div>

            {/* Formulaire Création Mission */}
            <form onSubmit={handleSubmit} className="bg-black/40 p-4 rounded-2xl border border-white/5 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <label className="text-[9px] text-slate-500 font-bold uppercase block mb-1">N° Train</label>
                        <input
                            type="text"
                            className="w-full bg-slate-900 border border-white/10 p-2 rounded text-white text-xs font-mono"
                            placeholder="804"
                            value={trainNumber}
                            onChange={(e) => setTrainNumber(e.target.value)}
                            required
                        />
                    </div>
                    <div>
                        <label className="text-[9px] text-slate-500 font-bold uppercase block mb-1">Ligne</label>
                        <input
                            type="text"
                            className="w-full bg-slate-900 border border-white/10 p-2 rounded text-white text-xs"
                            value={lineName}
                            onChange={(e) => setLineName(e.target.value)}
                        />
                    </div>
                </div>

                {/* Ajout des arrêts */}
                <div className="border-t border-white/5 pt-2">
                    <label className="text-[9px] text-cyan-500 font-bold uppercase block mb-2">Ajouter un arrêt</label>
                    <div className="flex gap-2 mb-2">
                        <select
                            className="flex-1 bg-slate-900 border border-white/10 p-2 rounded text-white text-xs"
                            value={currentStationName}
                            onChange={(e) => setCurrentStationName(e.target.value)}
                        >
                            {STATIONS.map(s => (
                                <option key={s.name} value={s.name}>{s.name} (PK {s.pk})</option>
                            ))}
                        </select>
                        <input
                            type="time"
                            className="w-20 bg-slate-900 border border-white/10 p-2 rounded text-white text-xs"
                            value={arrivalTime}
                            onChange={(e) => setArrivalTime(e.target.value)}
                        />
                        <button
                            type="button"
                            onClick={handleAddStop}
                            className="bg-cyan-500/20 text-cyan-500 px-3 py-1 rounded border border-cyan-500/50 text-xs font-bold hover:bg-cyan-500 hover:text-white transition-colors"
                        >
                            +
                        </button>
                    </div>

                    {/* Liste des arrêts temporaires */}
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                        {stops.map((stop, idx) => (
                            <div key={idx} className="flex justify-between items-center bg-white/5 p-2 rounded text-xs text-slate-300">
                                <span>{stop.stationName} <span className="text-slate-500">({stop.arrivalTime})</span></span>
                                <button type="button" onClick={() => handleRemoveStop(idx)} className="text-red-500 hover:text-white">×</button>
                            </div>
                        ))}
                    </div>
                </div>

                <button
                    type="submit"
                    className="w-full bg-cyan-600 hover:bg-cyan-500 text-white py-3 rounded-xl font-bold uppercase text-[10px] tracking-wider transition-all shadow-lg"
                    disabled={stops.length === 0}
                >
                    Créer la Mission
                </button>
            </form>

            {/* Liste des Missions Existantes */}
            <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                {missions.map(mission => (
                    <div key={mission.id} className="bg-slate-800/50 p-3 rounded-xl border border-white/5">
                        {editingMissionId === mission.id ? (
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="text-[9px] text-slate-500 font-bold uppercase block mb-1">N° Train</label>
                                        <input
                                            type="text"
                                            className="w-full bg-slate-900 border border-white/10 p-2 rounded text-white text-xs font-mono"
                                            value={editTrainNumber}
                                            onChange={(e) => setEditTrainNumber(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[9px] text-slate-500 font-bold uppercase block mb-1">Ligne</label>
                                        <input
                                            type="text"
                                            className="w-full bg-slate-900 border border-white/10 p-2 rounded text-white text-xs"
                                            value={editLineName}
                                            onChange={(e) => setEditLineName(e.target.value)}
                                        />
                                    </div>
                                </div>

                                {/* Ajout d'arrêt en édition */}
                                <div className="border border-white/5 rounded p-2">
                                    <div className="flex gap-2">
                                        <select
                                            className="flex-1 bg-slate-900 border border-white/10 p-1 rounded text-white text-[10px]"
                                            value={editCurrentStation}
                                            onChange={(e) => setEditCurrentStation(e.target.value)}
                                        >
                                            {STATIONS.map(s => <option key={s.name} value={s.name}>{s.name} (PK {s.pk})</option>)}
                                        </select>
                                        <input
                                            type="time"
                                            className="w-16 bg-slate-900 border border-white/10 p-1 rounded text-white text-[10px]"
                                            value={editArrivalTime}
                                            onChange={(e) => setEditArrivalTime(e.target.value)}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const station = STATIONS.find(s => s.name === editCurrentStation);
                                                if (station) {
                                                    setEditStops([...editStops, { stationName: station.name, arrivalTime: editArrivalTime, pk: station.pk }].sort((a, b) => a.arrivalTime.localeCompare(b.arrivalTime)));
                                                }
                                            }}
                                            className="bg-cyan-500/20 text-cyan-500 px-2 rounded text-xs"
                                        >+</button>
                                    </div>
                                </div>

                                <div className="space-y-1 border-l-2 border-cyan-500/30 pl-2">
                                    {editStops.map((stop, i) => (
                                        <div key={i} className="text-[10px] text-slate-400 flex justify-between items-center group">
                                            <span>{stop.stationName}</span>
                                            <div className="flex items-center gap-2">
                                                <span className="font-mono text-cyan-500">{stop.arrivalTime}</span>
                                                <button onClick={() => setEditStops(editStops.filter((_, idx) => idx !== i))} className="text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="flex gap-2 justify-end mt-2">
                                    <button onClick={handleCancelEdit} className="text-[10px] text-slate-500 px-3 py-1 uppercase font-bold text-xs hover:text-white">Annuler</button>
                                    <button onClick={handleSaveEdit} className="bg-green-500 px-3 py-1 rounded shadow text-white font-bold uppercase text-[10px]">Enregistrer</button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="flex justify-between items-start mb-2">
                                    <div>
                                        <h3 className="text-white font-bold text-xs">Train n°{mission.trainNumber}</h3>
                                        <p className="text-[10px] text-slate-500">{mission.lineName}</p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={() => handleStartEdit(mission)} className="text-cyan-500 text-xs hover:text-white transition-colors">Modifier</button>
                                        <button onClick={() => {
                                            console.log(`[CLIENT] Demande de suppression pour la mission ID: ${mission.id} (Train N°${mission.trainNumber})`);
                                            onDelete(mission.id);
                                        }} className="text-red-500 text-xs hover:text-white transition-colors">Supprimer</button>
                                    </div>
                                </div>
                                <div className="space-y-1 border-l-2 border-cyan-500/30 pl-2">
                                    {mission.stops.map((stop, i) => (
                                        <div key={i} className="text-[10px] text-slate-400 flex justify-between">
                                            <span>{stop.stationName}</span>
                                            <span className="font-mono text-cyan-500">{stop.arrivalTime}</span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
